import { el } from './dom.js';
import { ct } from './i18n.js';
import { ApiError } from './api.js';

/**
 * Notifications.
 *
 * The old interface had no visual feedback at all: after clicking "Save",
 * nothing said whether the operation had succeeded, failed, or was still
 * running.
 */

type ToastKind = 'success' | 'error' | 'info';

const STYLES: Record<ToastKind, string> = {
  success: 'alert-success',
  error: 'alert-error',
  info: 'alert-info',
};

const DURATIONS: Record<ToastKind, number> = {
  success: 3500,
  // An error stays longer: there has to be time to read it.
  error: 7000,
  info: 4000,
};

/** A way out of the message, when there is one. */
export interface ToastAction {
  label: string;
  href: string;
}

export function toast(message: string, kind: ToastKind = 'info', action?: ToastAction): void {
  const container = document.getElementById('toast-container');
  if (!container) return;

  const element = document.createElement('div');
  element.className = `alert ${STYLES[kind]} shadow-lg transition-opacity duration-300`;
  element.setAttribute('role', kind === 'error' ? 'alert' : 'status');

  const text = document.createElement('span');
  text.textContent = message;
  element.append(text);

  if (action) {
    element.append(
      el('a', {
        class: 'btn btn-sm btn-neutral ml-2 shrink-0',
        text: action.label,
        attrs: { href: action.href },
      }),
    );
  }

  container.append(element);

  /*
   * A toast carrying a way out is not dismissed on a timer.
   *
   * The others are notices: they say what happened and go. This one asks the
   * reader to decide something, and taking the button away mid-thought is how
   * an offer becomes an irritation.
   */
  if (action) return;

  setTimeout(() => {
    element.classList.add('opacity-0');
    setTimeout(() => element.remove(), 300);
  }, DURATIONS[kind]);
}

export const toastSuccess = (message: string) => toast(message, 'success');

/**
 * The one refusal on this dashboard that is also an offer.
 *
 * A quota is not a fault: the administrator did exactly what they meant to,
 * and the service said there is no room left. Answering that with a red toast
 * that fades in seven seconds tells them they did something wrong and leaves
 * them nowhere to go — while the thing they wanted is, at that exact instant,
 * for sale.
 *
 * Handled here rather than on each page, because there are twenty pages that
 * can hit a wall and this is the single function all of them already call.
 */
function upgradeAction(error: unknown): ToastAction | undefined {
  if (!(error instanceof ApiError) || error.code !== 'QUOTA_EXCEEDED') return undefined;

  // The server the visitor is standing on, so the offer lands on their own
  // subscription page rather than on a generic price list.
  const guildId = new URLSearchParams(window.location.search).get('guildId') ?? '';

  return {
    label: ct('quotaSeePremium', 'See Premium'),
    href: guildId ? `/premium?guildId=${encodeURIComponent(guildId)}` : '/premium',
  };
}

export const toastError = (error: unknown) => {
  toast(
    error instanceof Error ? error.message : ct('unexpectedError', 'An unexpected error occurred.'),
    'error',
    upgradeAction(error),
  );
};

/**
 * Confirmation of a destructive action.
 *
 * A `<dialog>` rather than `window.confirm()`: the native box cannot be
 * translated - its text follows the browser's language, not the site's - and
 * its appearance clashes with the rest of the interface. It also blocked the
 * execution thread, hence the asynchronous signature here.
 */
export function confirmAction(message: string): Promise<boolean> {
  return new Promise((resolve) => {
    const dialog = el('dialog', { class: 'modal' });

    const box = el('div', { class: 'modal-box max-w-md' });
    box.append(el('p', { class: 'py-2 text-sm leading-relaxed', text: message }));

    const actions = el('div', { class: 'modal-action' });

    const cancel = el('button', {
      class: 'btn btn-ghost btn-sm',
      text: ct('cancel', 'Cancel'),
      attrs: { type: 'button' },
    });
    const confirm = el('button', {
      class: 'btn btn-error btn-sm',
      text: ct('confirm', 'Confirm'),
      attrs: { type: 'button' },
    });

    const close = (result: boolean) => {
      dialog.close();
      dialog.remove();
      resolve(result);
    };

    cancel.addEventListener('click', () => close(false));
    confirm.addEventListener('click', () => close(true));

    // Escape and a click on the backdrop count as a refusal.
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close(false);
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close(false);
    });

    actions.append(cancel, confirm);
    box.append(actions);
    dialog.append(box);

    document.body.append(dialog);
    dialog.showModal();
    confirm.focus();
  });
}
